// src/lib/dealLabel.js — Canonical deal-labeling protocol.
//
// THE PROBLEM: a deal's stored `title` is just the IP/project name ("El Guapo").
// When two clients have deals on the same project (Sandra Equihua vs Jorge
// Gutierrez on El Guapo), bare titles are indistinguishable in any compact list.
//
// THE RULE (chosen 2026-06-24): never bake the distinguisher into the stored
// title — the title stays the IP (relational anchor). Derive the human label
// here, leading with whatever ISN'T already implied by the surface:
//
//   context 'global'  (lists, sidebar, search, pickers, Janet) → "Project — Client · Buyer"
//   context 'project' (on the project's own page)              → "Client · Buyer"
//   context 'client'  (on the client / contact / company page) → "Project · Buyer"
//
// The buyer (counterparty) is ALWAYS shown when known. deal_type/year append
// only as a further tiebreaker when the same client+buyer collide on a project
// (pass { showType:true } from a list that detects the collision).
//
// Field reality in this DB: the client lives in talent_name (or the talent/
// talent_company join); the buyer is almost always in `counterpart`, NOT
// studio_name (studio_name is empty on ~93% of deals). So buyer resolution must
// fall through to counterpart. Any caller must select `counterpart` to get it.

const TYPE_LABELS = {
  employment: 'employment',
  option: 'option',
  first_look: 'first-look',
  'shopping agreement': 'shopping',
  shopping_agreement: 'shopping',
  rights: 'rights',
  packaging: 'packaging',
  co_production: 'co-pro',
}

/** The represented party: talent contact > talent company > free-text talent_name. */
export function dealClient(deal) {
  if (!deal) return null
  if (deal.talent_type === 'company') return deal.talent_company?.name || deal.talent_name || null
  if (deal.talent && (deal.talent.first_name || deal.talent.last_name)) {
    return `${deal.talent.first_name || ''} ${deal.talent.last_name || ''}`.trim()
  }
  return deal.talent_name || null
}

/** The counterparty / buyer: studio join > studio_name > counterpart (the real home). */
export function dealBuyer(deal) {
  if (!deal) return null
  return deal.studio?.name || deal.studio_name || deal.counterpart || null
}

/** The IP / project anchor: stored title first, then the linked project/property. */
export function dealProject(deal) {
  if (!deal) return ''
  return deal.title || deal.project?.name || deal.project_name || deal.property?.title || ''
}

function typeTag(deal) {
  const t = deal?.deal_type
  return t ? (TYPE_LABELS[t] || String(t).replace(/_/g, ' ')) : null
}

/**
 * Canonical, context-aware deal label.
 * @param {object} deal
 * @param {object} [opts]
 * @param {'global'|'project'|'client'} [opts.context='global']
 * @param {boolean} [opts.showType=false] append the deal_type as a tiebreaker
 * @returns {string}
 */
export function dealLabel(deal, { context = 'global', showType = false } = {}) {
  if (!deal) return ''
  const project = dealProject(deal)
  const client = dealClient(deal)
  const buyer = dealBuyer(deal)
  const type = showType ? typeTag(deal) : null

  if (context === 'project') {
    return [client || 'Chatrone (producer)', buyer, type].filter(Boolean).join(' · ')
  }
  if (context === 'client') {
    return [project || '(untitled)', buyer, type].filter(Boolean).join(' · ')
  }
  // global: Project — Client · Buyer
  const right = [client, buyer, type].filter(Boolean).join(' · ')
  if (project && right) return `${project} — ${right}`
  return project || right || '(untitled deal)'
}

/**
 * Two-line split for compact list rows that render a title + muted subtitle.
 * Mirrors dealLabel's context rule: line 1 leads, line 2 carries the rest.
 *   global  → { title: Project,         subtitle: "Client · Buyer" }
 *   project → { title: Client,          subtitle: "Buyer" }
 *   client  → { title: Project,         subtitle: "Buyer" }
 */
export function dealLabelParts(deal, { context = 'global', showType = false } = {}) {
  if (!deal) return { title: '(untitled deal)', subtitle: '' }
  const project = dealProject(deal)
  const client = dealClient(deal)
  const buyer = dealBuyer(deal)
  const type = showType ? typeTag(deal) : null

  if (context === 'project') {
    return { title: client || 'Chatrone (producer)', subtitle: [buyer, type].filter(Boolean).join(' · ') }
  }
  if (context === 'client') {
    return { title: project || '(untitled)', subtitle: [buyer, type].filter(Boolean).join(' · ') }
  }
  return { title: project || client || '(untitled deal)', subtitle: [project ? client : null, buyer, type].filter(Boolean).join(' · ') }
}

/** Fields every caller should SELECT so the label resolves fully. */
export const DEAL_LABEL_FIELDS = 'id, title, deal_type, talent_name, talent_type, studio_name, counterpart, project_name'
